import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  Row,
  Select,
  Space,
  Tabs,
  Typography,
  Upload,
  message,
} from 'antd';
import type { FormProps, UploadProps } from 'antd';
import { DeleteOutlined, PaperClipOutlined, RobotOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ProductFormRenderer from '../components/ProductFormRenderer';
import { listDefectTags, listProductUnits } from '../services/api';
import type { AttachmentCategory, DefectTag, IncidentAttachment, IncidentCreate, IncidentFormSchema, ProductUnitOption } from '../types/incident';

const { Title, Text } = Typography;
const { TextArea } = Input;
const requiredFieldsByTab = {
  overview: ['title', 'trouble_date', 'claim_receive_date', 'info_classification', 'severity', 'claim_class', 'initial_comment'],
  product: ['model_name', 'model_number', 'serial_no', 'lot_number', 'phenomenon', 'temporary_action'],
  site: ['installation_environment'],
} as const;

const fieldToTab: Record<string, string> = {
  title: 'overview',
  trouble_date: 'overview',
  claim_receive_date: 'overview',
  info_classification: 'overview',
  severity: 'overview',
  claim_class: 'overview',
  initial_comment: 'overview',
  model_name: 'product',
  model_number: 'product',
  serial_no: 'product',
  lot_number: 'product',
  phenomenon: 'product',
  temporary_action: 'product',
  installation_environment: 'site',
};

type IncidentCreateFormValues = Omit<
  IncidentCreate,
  'commissioning_date' | 'trouble_date' | 'claim_receive_date' | 'production_factory_setting_date' | 'defect_tag_ids'
> & {
  commissioning_date?: Dayjs | null;
  trouble_date?: Dayjs | null;
  claim_receive_date?: Dayjs | null;
  production_factory_setting_date?: Dayjs | null;
  defect_location_tag_ids?: string[];
  defect_phenomenon_tag_ids?: string[];
  product_form_response?: Record<string, unknown>;
};

type PendingAttachment = {
  id: string;
  file: File;
  category: AttachmentCategory;
  description: string;
};

interface Props {
  onCreate: (payload: IncidentCreate) => Promise<string>;
  onDraft: (text: string) => Promise<Partial<IncidentCreate>>;
  onAttachFile: (
    incidentId: string,
    file: File,
    options?: { category?: AttachmentCategory; description?: string | null }
  ) => Promise<IncidentAttachment>;
}

const attachmentCategories: AttachmentCategory[] = ['PHOTO', 'VIDEO', 'LOG', 'CHECKER_DATA', 'OTHER'];

const inferAttachmentCategory = (file: File): AttachmentCategory => {
  const contentType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  if (contentType.startsWith('image/')) return 'PHOTO';
  if (contentType.startsWith('video/')) return 'VIDEO';
  if (fileName.endsWith('.csv') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return 'CHECKER_DATA';
  if (fileName.endsWith('.log') || fileName.endsWith('.txt') || fileName.endsWith('.json')) return 'LOG';
  return 'OTHER';
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const IncidentCreatePage = ({ onCreate, onDraft, onAttachFile }: Props) => {
  const { i18n, t } = useTranslation();
  const [form] = Form.useForm<IncidentCreateFormValues>();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [activeTab, setActiveTab] = useState('overview');
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [productUnits, setProductUnits] = useState<ProductUnitOption[]>([]);
  const [productUnitsLoading, setProductUnitsLoading] = useState(false);
  const [defectTags, setDefectTags] = useState<DefectTag[]>([]);
  const [defectTagsLoading, setDefectTagsLoading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const watchedValues = Form.useWatch([], form);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setProductUnitsLoading(true);
    listProductUnits()
      .then((items) => {
        if (!cancelled) setProductUnits(items);
      })
      .catch(() => {
        if (!cancelled) messageApi.warning(t('incident.create.messages.productMasterUnavailable'));
      })
      .finally(() => {
        if (!cancelled) setProductUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [messageApi, t]);

  useEffect(() => {
    let cancelled = false;
    setDefectTagsLoading(true);
    listDefectTags()
      .then((items) => {
        if (!cancelled) setDefectTags(items);
      })
      .catch(() => {
        if (!cancelled) messageApi.warning(t('incident.create.messages.defectTagsUnavailable'));
      })
      .finally(() => {
        if (!cancelled) setDefectTagsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [messageApi, t]);

  const selectedModelName = Form.useWatch('model_name', form);
  const selectedModelNumber = Form.useWatch('model_number', form);
  const selectedSerialNo = Form.useWatch('serial_no', form);
  const selectedProductId = Form.useWatch('product_id', form);

  const selectedProductForm = useMemo<IncidentFormSchema | null>(() => {
    const unit = productUnits.find((item) => item.product_id === selectedProductId);
    return unit?.incident_form_schema || null;
  }, [productUnits, selectedProductId]);

  const modelNameOptions = useMemo(
    () =>
      Array.from(new Set(productUnits.map((item) => item.product_name))).map((value) => ({
        value,
        label: value,
      })),
    [productUnits]
  );

  const modelNumberOptions = useMemo(() => {
    const filtered = productUnits.filter((item) => !selectedModelName || item.product_name === selectedModelName);
    return Array.from(new Set(filtered.map((item) => item.model_number))).map((value) => ({
      value,
      label: value,
    }));
  }, [productUnits, selectedModelName]);

  const serialNoOptions = useMemo(() => {
    const filtered = productUnits.filter(
      (item) =>
        (!selectedModelName || item.product_name === selectedModelName) &&
        (!selectedModelNumber || item.model_number === selectedModelNumber)
    );
    return filtered.map((item) => ({
      value: item.serial_no,
      label: item.serial_no,
    }));
  }, [productUnits, selectedModelName, selectedModelNumber]);

  const lotNumberOptions = useMemo(() => {
    const filtered = productUnits.filter(
      (item) =>
        (!selectedModelName || item.product_name === selectedModelName) &&
        (!selectedModelNumber || item.model_number === selectedModelNumber) &&
        (!selectedSerialNo || item.serial_no === selectedSerialNo)
    );
    return Array.from(new Set(filtered.map((item) => item.lot_number).filter(Boolean))).map((value) => ({
      value: value as string,
      label: value as string,
    }));
  }, [productUnits, selectedModelName, selectedModelNumber, selectedSerialNo]);

  const tagLabel = useCallback(
    (tag: DefectTag) => (i18n.language.startsWith('ja') ? tag.label_ja : tag.label_en),
    [i18n.language]
  );

  const defectLocationTagOptions = useMemo(
    () =>
      defectTags
        .filter((tag) => tag.category === 'LOCATION')
        .map((tag) => ({ value: tag.id, label: tagLabel(tag) })),
    [defectTags, tagLabel]
  );

  const defectPhenomenonTagOptions = useMemo(
    () =>
      defectTags
        .filter((tag) => tag.category === 'PHENOMENON')
        .map((tag) => ({ value: tag.id, label: tagLabel(tag) })),
    [defectTags, tagLabel]
  );

  const applyProductUnit = (unit: ProductUnitOption) => {
    form.setFieldsValue({
      product_id: unit.product_id,
      product_unit_id: unit.product_unit_id,
      model_name: unit.product_name,
      model_number: unit.model_number,
      serial_no: unit.serial_no,
      lot_number: unit.lot_number || undefined,
      production_factory: unit.factory_name || undefined,
      product_form_response: {},
    });
  };

  const clearProductUnitSelection = () => {
    form.setFieldsValue({
      product_id: undefined,
      product_unit_id: undefined,
      serial_no: undefined,
      lot_number: undefined,
      production_factory: undefined,
      product_form_response: {},
    });
  };

  const normalizeOptionalText = (value: string | null | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed || undefined;
  };

  const formatDate = (value: Dayjs | null | undefined): string | null =>
    value ? value.format('YYYY-MM-DD') : null;

  const normalizeProductFormResponse = (schema: IncidentFormSchema | null, response?: Record<string, unknown>): Record<string, unknown> => {
    if (!schema) return {};
    return schema.fields.reduce<Record<string, unknown>>((acc, field) => {
      if (field.type === 'section') return acc;
      const value = response?.[field.id];
      if (value === undefined || value === null || value === '') return acc;
      if (field.type === 'date' && dayjs.isDayjs(value)) {
        acc[field.id] = value.format('YYYY-MM-DD');
      } else {
        acc[field.id] = value;
      }
      return acc;
    }, {});
  };

  const composeSubject = (values: Partial<IncidentCreateFormValues>): string => {
    const cause = values.info_classification || t('incident.create.subjectFallback.cause');
    const location = values.defect_location || values.installation_environment || t('incident.create.subjectFallback.location');
    const model = values.model_name || values.model_number || t('incident.create.subjectFallback.model');
    const symptom = [values.phenomenon?.slice(0, 40), values.error_code].filter(Boolean).join(' / ');
    return `[${cause}] ${location}; ${model}; ${symptom || t('incident.create.subjectFallback.symptom')}`;
  };

  const buildSubject = () => {
    const subject = composeSubject(form.getFieldsValue());
    setGeneratedSubject(subject);
    form.setFieldValue('title', subject);
  };

  const maybeAutoGenerateSubject = (changedValues: Partial<IncidentCreateFormValues>, allValues: IncidentCreateFormValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, 'title')) return;
    const currentTitle = allValues.title?.trim() || '';
    if (currentTitle && currentTitle !== generatedSubject) return;
    const subject = composeSubject(allValues);
    setGeneratedSubject(subject);
    form.setFieldValue('title', subject);
  };

  const validateDateOrder = () => {
    const values = form.getFieldsValue();
    const commissioningDate = values.commissioning_date;
    const troubleDate = values.trouble_date;
    const claimReceiveDate = values.claim_receive_date;
    if (commissioningDate && troubleDate && troubleDate.isBefore(commissioningDate, 'day')) {
      return Promise.reject(new Error(t('incident.create.messages.troubleDateOrder')));
    }
    if (troubleDate && claimReceiveDate && claimReceiveDate.isBefore(troubleDate, 'day')) {
      return Promise.reject(new Error(t('incident.create.messages.claimReceiveDateOrder')));
    }
    if (commissioningDate && claimReceiveDate && claimReceiveDate.isBefore(commissioningDate, 'day')) {
      return Promise.reject(new Error(t('incident.create.messages.claimReceiveAfterCommissioning')));
    }
    return Promise.resolve();
  };

  const isMissingRequiredValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return !value.trim();
    return false;
  };

  const countMissingFields = (fields: readonly string[]): number =>
    fields.filter((field) => {
      const fieldKey = field as keyof IncidentCreateFormValues;
      const value = watchedValues ? watchedValues[fieldKey] : form.getFieldValue(fieldKey);
      return isMissingRequiredValue(value);
    }).length;

  const formatTabLabel = (label: string, missingCount: number) =>
    missingCount > 0 ? `${label} (${missingCount})` : label;

  const countMissingProductFormFields = (): number => {
    if (!selectedProductForm) return 0;
    const response = (form.getFieldValue('product_form_response') || {}) as Record<string, unknown>;
    return selectedProductForm.fields.filter((field) => field.type !== 'section' && field.required && isMissingRequiredValue(response[field.id])).length;
  };

  const handleDraft = async () => {
    const text = form.getFieldValue('phenomenon');
    if (!text) {
      messageApi.warning(t('incident.create.messages.phenomenonRequired'));
      return;
    }
    const draft = await onDraft(text);
    form.setFieldsValue(draft);
  };

  const handleFinish = async (values: IncidentCreateFormValues) => {
    const { defect_location_tag_ids, defect_phenomenon_tag_ids, ...incidentValues } = values;
    const payload: IncidentCreate = {
      ...incidentValues,
      title: values.title.trim(),
      phenomenon: values.phenomenon.trim(),
      trouble_date: formatDate(values.trouble_date) || dayjs().format('YYYY-MM-DD'),
      claim_receive_date: formatDate(values.claim_receive_date) || dayjs().format('YYYY-MM-DD'),
      commissioning_date: formatDate(values.commissioning_date),
      production_factory_setting_date: formatDate(values.production_factory_setting_date),
      installation_environment: values.installation_environment.trim(),
      info_classification: values.info_classification.trim(),
      initial_comment: values.initial_comment.trim(),
      claim_class: values.claim_class.trim(),
      defect_location: normalizeOptionalText(values.defect_location),
      site_resolution_status: normalizeOptionalText(values.site_resolution_status),
      model_name: values.model_name.trim(),
      model_number: values.model_number.trim(),
      serial_no: values.serial_no.trim(),
      lot_number: values.lot_number.trim(),
      production_factory: normalizeOptionalText(values.production_factory),
      season: normalizeOptionalText(values.season),
      piping_length: normalizeOptionalText(values.piping_length),
      error_code: normalizeOptionalText(values.error_code),
      room_size: normalizeOptionalText(values.room_size),
      dealer: normalizeOptionalText(values.dealer),
      delivery_destination: normalizeOptionalText(values.delivery_destination),
      installer_name: normalizeOptionalText(values.installer_name),
      delivery_route: normalizeOptionalText(values.delivery_route),
      service_control_no: normalizeOptionalText(values.service_control_no),
      customer_impact: normalizeOptionalText(values.customer_impact),
      temporary_action: values.temporary_action.trim(),
      product_form_response: normalizeProductFormResponse(selectedProductForm, values.product_form_response),
      defect_tag_ids: [...(defect_location_tag_ids || []), ...(defect_phenomenon_tag_ids || [])],
    };
    setSubmitting(true);
    try {
      const incidentId = await onCreate(payload);
      for (const attachment of pendingAttachments) {
        await onAttachFile(incidentId, attachment.file, {
          category: attachment.category,
          description: attachment.description.trim() || null,
        });
      }
      navigate(`/incidents/${incidentId}`);
    } catch (error) {
      messageApi.error(t('incident.create.messages.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinishFailed: FormProps<IncidentCreateFormValues>['onFinishFailed'] = ({ errorFields }) => {
    const firstErrorName = errorFields[0]?.name?.[0];
    if (firstErrorName === 'product_form_response') {
      setActiveTab('productSpecific');
      return;
    }
    if (typeof firstErrorName === 'string') {
      setActiveTab(fieldToTab[firstErrorName] || 'overview');
      window.setTimeout(() => form.scrollToField(firstErrorName), 0);
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      if (file.size > 50 * 1024 * 1024) {
        messageApi.error(t('app.messages.attachmentTooLarge'));
        return Upload.LIST_IGNORE;
      }
      setPendingAttachments((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          file,
          category: inferAttachmentCategory(file),
          description: '',
        },
      ]);
      return Upload.LIST_IGNORE;
    },
    multiple: true,
    showUploadList: false,
  };

  const updatePendingAttachment = (id: string, values: Partial<Pick<PendingAttachment, 'category' | 'description'>>) => {
    setPendingAttachments((current) =>
      current.map((attachment) => (attachment.id === id ? { ...attachment, ...values } : attachment))
    );
  };

  return (
    <div className="page">
      {messageContextHolder}
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onFinishFailed={handleFinishFailed}
          onValuesChange={maybeAutoGenerateSubject}
          initialValues={{
            severity: 'medium',
            claim_class: 'standard',
            trouble_date: dayjs(),
            claim_receive_date: dayjs(),
          }}
        >
          <Row gutter={[24, 24]} align="top">
            <Col xs={24} lg={16}>
              <Tabs
                activeKey={activeTab}
                className="createTabs"
                onChange={setActiveTab}
                items={[
                  {
                    key: 'overview',
                    label: formatTabLabel(t('incident.create.tabs.overview'), countMissingFields(requiredFieldsByTab.overview)),
                    forceRender: true,
                    children: (
                      <div className="formSection">
                        <Form.Item label={t('incident.create.fields.subject')} name="title" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label={t('incident.create.fields.troubleDate')}
                              name="trouble_date"
                              dependencies={['commissioning_date', 'claim_receive_date']}
                              rules={[{ required: true }, { validator: validateDateOrder }]}
                            >
                              <DatePicker className="fullWidthControl" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label={t('incident.create.fields.claimReceiveDate')}
                              name="claim_receive_date"
                              dependencies={['commissioning_date', 'trouble_date']}
                              rules={[{ required: true }, { validator: validateDateOrder }]}
                            >
                              <DatePicker className="fullWidthControl" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.infoClassification')} name="info_classification" rules={[{ required: true }]}>
                              <Select
                                options={[
                                  { value: 'Field claim', label: t('incident.classification.fieldClaim') },
                                  { value: 'Service request', label: t('incident.classification.serviceRequest') },
                                  { value: 'Quality escalation', label: t('incident.classification.qualityEscalation') },
                                  { value: 'Parts request', label: t('incident.classification.partsRequest') },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item label={t('incident.create.fields.severity')} name="severity" rules={[{ required: true }]}>
                              <Select
                                options={['low', 'medium', 'high', 'critical'].map((value) => ({
                                  value,
                                  label: t(`incident.severity.${value}`),
                                }))}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item label={t('incident.create.fields.claimClass')} name="claim_class" rules={[{ required: true }]}>
                              <Select
                                options={[
                                  { value: 'standard', label: t('incident.claimClass.standard') },
                                  { value: 'A', label: t('incident.claimClass.A') },
                                  { value: 'S', label: t('incident.claimClass.S') },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item label={t('incident.create.fields.initialComment')} name="initial_comment" rules={[{ required: true }]}>
                          <TextArea rows={4} />
                        </Form.Item>
                      </div>
                    ),
                  },
                  {
                    key: 'product',
                    label: formatTabLabel(t('incident.create.tabs.product'), countMissingFields(requiredFieldsByTab.product)),
                    forceRender: true,
                    children: (
                      <div className="formSection">
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.modelName')} name="model_name" rules={[{ required: true }]}>
                              <Select
                                allowClear
                                showSearch
                                loading={productUnitsLoading}
                                options={modelNameOptions}
                                optionFilterProp="label"
                                onChange={() => {
                                  form.setFieldsValue({ model_number: undefined });
                                  clearProductUnitSelection();
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.modelNumber')} name="model_number" rules={[{ required: true }]}>
                              <Select
                                allowClear
                                showSearch
                                disabled={!selectedModelName}
                                loading={productUnitsLoading}
                                options={modelNumberOptions}
                                optionFilterProp="label"
                                onChange={() => clearProductUnitSelection()}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.serialNo')} name="serial_no" rules={[{ required: true }]}>
                              <Select
                                allowClear
                                showSearch
                                disabled={!selectedModelNumber}
                                loading={productUnitsLoading}
                                options={serialNoOptions}
                                optionFilterProp="label"
                                onChange={(value) => {
                                  const unit = productUnits.find(
                                    (item) =>
                                      item.product_name === selectedModelName &&
                                      item.model_number === selectedModelNumber &&
                                      item.serial_no === value
                                  );
                                  if (unit) applyProductUnit(unit);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.lotNumber')} name="lot_number" rules={[{ required: true }]}>
                              <Select
                                allowClear
                                disabled={!selectedSerialNo}
                                loading={productUnitsLoading}
                                options={lotNumberOptions}
                                onChange={(value) => {
                                  const unit = productUnits.find(
                                    (item) =>
                                      item.product_name === selectedModelName &&
                                      item.model_number === selectedModelNumber &&
                                      item.serial_no === selectedSerialNo &&
                                      item.lot_number === value
                                  );
                                  if (unit) applyProductUnit(unit);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.unitCount')} name="unit_count">
                              <InputNumber className="fullWidthControl" min={0} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.errorCode')} name="error_code">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.defectLocationTags')} name="defect_location_tag_ids">
                              <Select
                                allowClear
                                mode="multiple"
                                loading={defectTagsLoading}
                                options={defectLocationTagOptions}
                                optionFilterProp="label"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.defectPhenomenonTags')} name="defect_phenomenon_tag_ids">
                              <Select
                                allowClear
                                mode="multiple"
                                loading={defectTagsLoading}
                                options={defectPhenomenonTagOptions}
                                optionFilterProp="label"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item label={t('incident.create.fields.defectPhenomena')} name="phenomenon" rules={[{ required: true }]}>
                          <TextArea rows={5} />
                        </Form.Item>
                        <Form.Item label={t('incident.create.fields.temporaryAction')} name="temporary_action" rules={[{ required: true }]}>
                          <TextArea rows={3} />
                        </Form.Item>
                        <Form.Item name="product_id" hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name="product_unit_id" hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name="production_factory" hidden>
                          <Input />
                        </Form.Item>
                      </div>
                    ),
                  },
                  {
                    key: 'site',
                    label: formatTabLabel(t('incident.create.tabs.site'), countMissingFields(requiredFieldsByTab.site)),
                    forceRender: true,
                    children: (
                      <div className="formSection">
                        <Form.Item
                          label={t('incident.create.fields.installationEnvironment')}
                          name="installation_environment"
                          rules={[{ required: true }]}
                        >
                          <TextArea rows={3} />
                        </Form.Item>
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.siteSolved')} name="site_resolution_status">
                              <Select
                                allowClear
                                options={['Solved', 'Temporary recovery', 'Not solved', 'Unknown'].map((value) => ({
                                  value,
                                  label: t(`incident.siteSolved.${value}`),
                                }))}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label={t('incident.create.fields.commissioningDate')}
                              name="commissioning_date"
                              dependencies={['trouble_date', 'claim_receive_date']}
                              rules={[{ validator: validateDateOrder }]}
                            >
                              <DatePicker className="fullWidthControl" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.season')} name="season">
                              <Select
                                allowClear
                                options={['Summer', 'Winter', 'All season', 'Off season'].map((value) => ({
                                  value,
                                  label: t(`incident.season.${value}`),
                                }))}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.pipingLength')} name="piping_length">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label={t('incident.create.fields.roomSize')} name="room_size">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item label={t('incident.create.fields.customerImpact')} name="customer_impact">
                          <TextArea rows={3} />
                        </Form.Item>
                      </div>
                    ),
                  },
                  ...(selectedProductForm
                    ? [
                        {
                          key: 'productSpecific',
                          label: formatTabLabel(t('incident.create.tabs.productSpecific'), countMissingProductFormFields()),
                          forceRender: true,
                          children: (
                            <div className="formSection productSpecificSection">
                              <div className="productSpecificIntro">
                                <Text strong>{t('incident.create.productSpecific.title')}</Text>
                                <Text type="secondary">{t('incident.create.productSpecific.description')}</Text>
                              </div>
                              <ProductFormRenderer schema={selectedProductForm} />
                            </div>
                          ),
                        },
                      ]
                    : []),
                  {
                    key: 'details',
                    label: t('incident.create.tabs.details'),
                    forceRender: true,
                    children: (
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item label={t('incident.create.fields.dealer')} name="dealer">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item label={t('incident.create.fields.deliveryDestination')} name="delivery_destination">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label={t('incident.create.fields.installerName')} name="installer_name">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label={t('incident.create.fields.deliveryRoute')} name="delivery_route">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label={t('incident.create.fields.serviceControlNo')} name="service_control_no">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    ),
                  },
                ]}
              />
            </Col>

            <Col xs={24} lg={8}>
              <div className="createAside">
                <Title level={4}>{t('incident.create.assist.title')}</Title>
                <Space direction="vertical" className="fullWidthControl">
                  <Button block onClick={buildSubject}>
                    {t('incident.create.actions.generateSubject')}
                  </Button>
                  <Button block icon={<RobotOutlined />} onClick={handleDraft}>
                    {t('incident.create.actions.aiDraft')}
                  </Button>
                </Space>
                <Divider />
                <Title level={5}>{t('incident.create.assist.requiredFields')}</Title>
                <Text type="secondary">{t('incident.create.assist.requiredCore')}</Text>
                <Divider />
                <Title level={5}>{t('incident.create.assist.responseTasks')}</Title>
                <Space wrap>
                  <Text code>SERVICE</Text>
                  <Text code>QUALITY</Text>
                </Space>
                <Divider />
                <Title level={5}>{t('incident.create.attachments.title')}</Title>
                <Upload.Dragger {...uploadProps} className="createAttachmentDropzone">
                  <p className="ant-upload-drag-icon">
                    <PaperClipOutlined />
                  </p>
                  <p className="ant-upload-text">{t('incident.create.attachments.dropTitle')}</p>
                  <p className="ant-upload-hint">{t('incident.create.attachments.dropHint')}</p>
                </Upload.Dragger>
                <List
                  className="createAttachmentList"
                  dataSource={pendingAttachments}
                  locale={{ emptyText: t('incident.create.attachments.empty') }}
                  renderItem={(attachment) => (
                    <List.Item
                      actions={[
                        <Button
                          aria-label={t('incident.create.attachments.remove')}
                          icon={<DeleteOutlined />}
                          size="small"
                          type="text"
                          onClick={() => setPendingAttachments((current) => current.filter((item) => item.id !== attachment.id))}
                        />,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<PaperClipOutlined />}
                        title={attachment.file.name}
                        description={
                          <Space direction="vertical" className="fullWidthControl" size={6}>
                            <Text type="secondary">
                              {formatBytes(attachment.file.size)} · {attachment.file.type || 'application/octet-stream'}
                            </Text>
                            <Select
                              value={attachment.category}
                              onChange={(category) => updatePendingAttachment(attachment.id, { category })}
                              options={attachmentCategories.map((category) => ({
                                value: category,
                                label: t(`incident.attachmentCategory.${category}`),
                              }))}
                            />
                            <Input
                              value={attachment.description}
                              placeholder={t('incident.create.attachments.description')}
                              onChange={(event) => updatePendingAttachment(attachment.id, { description: event.target.value })}
                            />
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
                <Divider />
                <Space direction="vertical" className="fullWidthControl">
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitting} block>
                    {t('incident.create.actions.submit')}
                  </Button>
                  <Button onClick={() => navigate('/incidents')} block>
                    {t('incident.create.actions.cancel')}
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default IncidentCreatePage;
